import React, { useState } from "react";
import { Button, FormHelperText, Grid } from "@mui/material";
import type { DateValidationError, PickerChangeHandlerContext } from "@mui/x-date-pickers";
import { DatePicker as _DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import type { DateTime } from "luxon";
import { Settings } from "luxon";
import { getOrParseDateTime } from "@cpt/shared";

export interface DatePickerProps {
  id?: string;
  minDate?: DateTime;
  maxDate?: DateTime;
  value?: DateTime | string;
  label: string;
  isInvalid?: boolean;
  onChange: (date: DateTime | null) => void;
  errorMessage?: string;
  required?: boolean;
  presets?: DatePickerPreset[];
  showLocalConversion: boolean;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  referenceDate?: DateTime;
  variant?: "standard" | "outlined" | "filled";
  hideLabel?: boolean;
  labelledBy?: string;
  size?: "small" | "medium";
}

export interface DatePickerPreset {
  label: string;
  getDate: () => DateTime;
}

function DatePickerInput(props: DatePickerProps) {
  const {
    id,
    minDate,
    maxDate,
    value,
    label,
    isInvalid,
    onChange,
    errorMessage,
    required,
    presets,
    showLocalConversion,
    disabled,
    inputRef,
    referenceDate,
    variant = "standard",
    hideLabel = false,
    labelledBy,
    size,
  } = props;

  const [hasValidationError, setHasValidationError] = useState(false);

  const handleClickClearInput = () => {
    onChange(null);
  };

  const setPreset = (preset: DatePickerPreset) => {
    onChange(preset.getDate());
  };

  const handleDateChange = (date: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => {
    setHasValidationError(!!context.validationError);

    if (date && date.isValid) {
      //For some reason, the date picker is returning the date in local time if you type it in, so we need to convert it to UTC while keeping the same value
      onChange(date.setZone("utc", { keepLocalTime: true }));
    } else {
      onChange(null);
    }
  };

  const error = isInvalid ?? hasValidationError;
  const normalizedValue = value ? getOrParseDateTime(value) : undefined;
  const fullLabel = `${label} (UTC)`;

  let helperText = null;

  if (showLocalConversion && normalizedValue?.isValid) {
    const localTime = normalizedValue.toLocal();
    helperText = `${localTime.toLocaleString()} (${localTime.zoneName})`;
  }

  if (isInvalid) {
    helperText = errorMessage;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={Settings.defaultLocale}>
      <_DatePicker
        onChange={handleDateChange}
        minDate={minDate}
        maxDate={maxDate}
        label={hideLabel ? undefined : fullLabel}
        value={normalizedValue?.isValid ? normalizedValue : null}
        inputRef={inputRef}
        disabled={disabled}
        referenceDate={referenceDate}
        slotProps={{
          textField: {
            id,
            variant,
            required,
            error,
            fullWidth: true,
            size,
            ...(hideLabel ? { slotProps: { input: { "aria-labelledby": labelledBy } } } : {}),
          },
          field: {
            clearable: true,
            onClear: handleClickClearInput,
          },
          openPickerButton: { size },
          clearButton: { size },
        }}
      />
      <Grid container>
        <Grid size={12}>
          <FormHelperText error={error}>{helperText}</FormHelperText>
        </Grid>
        {presets && (
          <Grid size={12}>
            {presets.map((preset, index) => (
              <Button
                sx={{ minWidth: 0, p: 0, mr: 1 }}
                key={index}
                size="small"
                variant="text"
                onClick={() => setPreset(preset)}
                disabled={disabled}
              >
                {preset.label}
              </Button>
            ))}
          </Grid>
        )}
      </Grid>
    </LocalizationProvider>
  );
}

export default DatePickerInput;
